import { APP_BASE_HREF, Location } from '@angular/common';
import {
  afterNextRender,
  DestroyRef,
  EnvironmentInjector,
  inject,
  NgZone,
  provideAppInitializer,
  type EnvironmentProviders,
} from '@angular/core';
import {
  NavigationCancel,
  NavigationEnd,
  NavigationError,
  NavigationSkipped,
  Router,
} from '@angular/router';
import { filter, take } from 'rxjs/operators';
import { isBrowserPathUnderPluginBase, PHOENIX_PLUGIN_URL_CHANGED_EVENT } from './plugin-router-url';

/**
 * Runs {@link Router.initialNavigation} after the next render, then follows host menu clicks.
 *
 * Needed when the app is bootstrapped as a custom element via {@link createApplication}
 * (no {@link ApplicationRef.bootstrap}), so the outlet exists before the first navigation.
 *
 * Later host navigations (`routerLink` in the PHX sidebar) update the browser URL with
 * `pushState`. This router only listens for `popstate`, so the URL changes and the previous
 * plugin page stays mounted. `phoenix-plugin-url-changed` is the host signal for that case.
 * Sync with `skipLocationChange` so the plugin does not write history (the host treats
 * `popstate` as Back).
 */
export function providePhoenixPluginRouterInitialNavigation(): EnvironmentProviders {
  return provideAppInitializer(() => {
    const router = inject(Router);
    const location = inject(Location);
    const baseHref = inject(APP_BASE_HREF);
    const injector = inject(EnvironmentInjector);
    const destroyRef = inject(DestroyRef);
    const ngZone = inject(NgZone);

    let initialNavigationSettled = false;

    const syncFromBrowser = (): void => {
      if (!initialNavigationSettled) {
        return;
      }
      if (!isBrowserPathUnderPluginBase(window.location.pathname, baseHref)) {
        return;
      }
      const target = location.path(true);
      const current = router.serializeUrl(router.parseUrl(router.url));
      const next = router.serializeUrl(router.parseUrl(target));
      if (current === next) {
        return;
      }
      // Host menu clicks run in the host zone. Enter this app's zone so the outlet updates.
      ngZone.run(() => {
        void router.navigateByUrl(target, { skipLocationChange: true });
      });
    };

    const onPluginUrlChanged = (): void => {
      syncFromBrowser();
    };
    window.addEventListener(PHOENIX_PLUGIN_URL_CHANGED_EVENT, onPluginUrlChanged);
    destroyRef.onDestroy(() => {
      window.removeEventListener(PHOENIX_PLUGIN_URL_CHANGED_EVENT, onPluginUrlChanged);
    });

    afterNextRender(() => {
      const subscription = router.events
        .pipe(
          filter(
            (event): event is NavigationEnd | NavigationCancel | NavigationError | NavigationSkipped =>
              event instanceof NavigationEnd
              || event instanceof NavigationCancel
              || event instanceof NavigationError
              || event instanceof NavigationSkipped,
          ),
          take(1),
        )
        .subscribe(() => {
          initialNavigationSettled = true;
          syncFromBrowser();
        });
      destroyRef.onDestroy(() => subscription.unsubscribe());

      router.initialNavigation();
      if (router.currentNavigation() == null) {
        initialNavigationSettled = true;
        syncFromBrowser();
      }
    }, { injector });
  });
}
